// @flow
import {List, fromJS, Map} from 'immutable';
import moment from 'moment';
import {setUpStore, Store} from 'client/framework';
import {type QueryBuilder, resource} from '@albert-io/json-api-framework/request/builder';
import SelectableDateRangeStore from 'generic/SelectableDateRange/SelectableDateRange.store';
import dashboardActions from './Dashboard.actions';

export class DashboardStore extends Store {
  constructor(name) {
    super(name);

    this.selectableDateRangeStore = setUpStore(
      SelectableDateRangeStore,
      'SchoolAdminDashboardSelectableDateRangeStore'
    );
    this.intialData = fromJS({
      selectedInterval: 'month',
      schoolId: ''
    });

    this.setInitialData(this.intialData);
    this.handle(dashboardActions.SET_SCHOOL_ID, this._setSchoolId);
    this.handle(dashboardActions.SET_SELECTED_INTERVAL, this._setSelectedInterval);
  }

  _setSchoolId(schoolId) {
    this.writeData('schoolId', schoolId);
  }

  _setSelectedInterval(interval: string) {
    this.writeData('selectedInterval', interval);
  }

  getSchoolQuery(): QueryBuilder {
    return resource('school_v5').mandarkEndpoint(['schools_v5', this.getSchoolId()]).customQuery({
      with_meta: 'school_v5'
    });
  }

  getCountOfPendingTeachersQuery(): QueryBuilder {
    return this.getSchoolQuery().meta({
      context: {
        school_personnel: {
          statuses: 'pending'
        },
        school: {
          id: this.getSchoolId()
        }
      }
    });
  }

  getSchoolWithChartDataQuery(): QueryBuilder {
    return this.getSchoolQuery().meta({
      context: {
        start_date: this.getStartDate(),
        end_date: this.getEndDate(),
        time_series_step: this.getSelectedInterval()
      }
    });
  }

  getSchoolId(): string {
    return this.readData('schoolId');
  }

  getSelectedInterval(): string {
    return this.readData('selectedInterval');
  }

  getSelectedIntervalFormat(): string {
    if (this.getSelectedInterval() === 'month') {
      return `MMM, YY`;
    }
    return `MMM DD, YY`;
  }

  getSchool() {
    return this.getSchoolQuery().getResource();
  }

  getSchoolWithChartData() {
    return this.getSchoolWithChartDataQuery().getResource();
  }

  getCountOfGuessesIntervals(): List<*> {
    if (!this.hasSchoolAdminChartData()) {
      return List();
    }

    return this.getSchoolWithChartData()
      .getMeta()
      .getCountOfGuessesIntervals()
      .map((interval) => {
        const date = interval.get('label').split(' - ');
        return Map({
          count: interval.get('count_of_guesses'),
          date: moment(date, this.getSelectedIntervalFormat())
        });
      });
  }

  getTotalTimeElapsedIntervals(): List<*> {
    if (!this.hasSchoolAdminChartData()) {
      return List();
    }

    return this.getSchoolWithChartData()
      .getMeta()
      .getTotalTimeElapsedIntervals()
      .map((interval) => {
        const date = interval.get('label').split(' - ');
        const timeElapsed = interval.get('total_time_elapsed');
        return Map({
          count: moment.duration(parseInt(timeElapsed, 10)).asHours().toFixed(2),
          date: moment(date, this.getSelectedIntervalFormat())
        });
      });
  }

  getSelectableDateRangeStoreName(): string {
    return this.selectableDateRangeStore.getName();
  }

  getDateRangeDropdownStore(): Map<string, *> | null {
    return this.selectableDateRangeStore.getSimpleDropdownStore();
  }

  getStartDate(): number {
    const startDate = this.getDateRangeDropdownStore().getSelectedItem().get('start_date', 0);
    /**
     * This is a bit of a hack to account for a bad default value...
     * @see hasSchoolAdminChartData
     */
    if (startDate === 0) {
      return 0;
    }
    return moment(startDate).startOf('day').valueOf();
  }

  getEndDate(): number {
    const endDate = this.getDateRangeDropdownStore().getSelectedItem().get('end_date', moment());
    return moment(endDate).endOf('day').valueOf();
  }

  hasSchoolAdminStatistics(): boolean {
    return this.getSchoolQuery().isResourcePopulated();
  }

  hasSchoolAdminChartData(): boolean {
    /**
     * Due to the way the default value is set for a `SelectableDateRange` and how
     * the `Dashboard.react` code is structured, on the initial render, the `DateRangeDropdownStore`
     * will return `0` as the selected `start_date`, this check (`this.getStartDate() !== 0`) ensures
     * we wait for the `SelectableDateRange.constructor` to run placing _that_ store in a proper
     * state for our query.
     */
    return this.getStartDate() !== 0 && this.getSchoolWithChartDataQuery().isResourcePopulated();
  }
}

export default new DashboardStore('DashboardStore');
